import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import axios from 'axios';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { 
  DollarSign, 
  TrendingUp, 
  Target, 
  Percent, 
  Package, 
  Clock,
  Users,
  Briefcase
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ['#007AFF', '#FF3B30', '#10B981', '#F59E0B', '#8B5CF6'];

const DashboardPage = () => {
  const { getAuthHeaders } = useAuth();
  const [overview, setOverview] = useState(null);
  const [teamData, setTeamData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [penetrationData, setPenetrationData] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const headers = getAuthHeaders();
        const [overviewRes, teamRes, productRes, penetrationRes, activityRes, stagesRes] = await Promise.all([
          axios.get(`${API}/dashboard/overview`, { headers }),
          axios.get(`${API}/dashboard/by-team-member`, { headers }),
          axios.get(`${API}/dashboard/by-product`, { headers }),
          axios.get(`${API}/dashboard/penetration-by-sport`, { headers }),
          axios.get(`${API}/dashboard/activity-kpis`, { headers }),
          axios.get(`${API}/dashboard/pipeline-by-stage`, { headers })
        ]);

        setOverview(overviewRes.data);
        setTeamData(teamRes.data);
        setProductData(productRes.data);
        setPenetrationData(penetrationRes.data);
        setActivityData(activityRes.data);
        setPipelineStages(stagesRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [getAuthHeaders]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse-slow text-[#007AFF] text-xl">Loading dashboard...</div>
      </div>
    );
  }

  const kpiCards = [
    {
      title: 'Pipeline Premium',
      value: formatCurrency(overview?.total_pipeline_premium || 0),
      icon: DollarSign,
      color: '#007AFF',
      bgColor: '#EFF6FF'
    },
    {
      title: 'Closed Premium',
      value: formatCurrency(overview?.total_closed_premium || 0),
      icon: TrendingUp,
      color: '#10B981',
      bgColor: '#ECFDF5'
    },
    {
      title: 'Open Opportunities',
      value: overview?.open_opportunities || 0,
      icon: Target,
      color: '#F59E0B',
      bgColor: '#FFFBEB'
    },
    {
      title: 'Win Rate',
      value: `${overview?.win_rate || 0}%`,
      icon: Percent,
      color: '#8B5CF6',
      bgColor: '#F5F3FF'
    },
    {
      title: 'Avg Premium/Opp',
      value: formatCurrency(overview?.avg_premium_per_opportunity || 0),
      icon: Package,
      color: '#FF3B30',
      bgColor: '#FEF2F2'
    },
    {
      title: 'Avg Sales Cycle',
      value: `${overview?.avg_sales_cycle_days || 0} days`,
      icon: Clock,
      color: '#007AFF',
      bgColor: '#EFF6FF'
    }
  ];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0F172A] tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            EXECUTIVE DASHBOARD
          </h1>
          <p className="text-[#475569] mt-1">Division I FBS/FCS Pipeline Overview</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiCards.map((kpi, index) => {
          const Icon = kpi.icon;
          return (
            <Card 
              key={kpi.title} 
              className="bg-white border-[#E2E8F0] hover:border-[#007AFF]/50 hover:shadow-md transition-all duration-300 kpi-card"
              data-testid={`kpi-card-${index}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div 
                    className="w-8 h-8 rounded-md flex items-center justify-center"
                    style={{ backgroundColor: kpi.bgColor }}
                  >
                    <Icon className="w-4 h-4" style={{ color: kpi.color }} />
                  </div>
                </div>
                <p className="text-xs text-[#475569] uppercase tracking-wider mb-1 font-medium">{kpi.title}</p>
                <p className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {kpi.value}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row 1 */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pipeline by Stage */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              PIPELINE BY STAGE
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipelineStages} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={true} vertical={false} />
                  <XAxis 
                    type="number" 
                    tick={{ fill: '#475569', fontSize: 12 }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="stage" 
                    tick={{ fill: '#475569', fontSize: 11 }}
                    width={120}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                    labelStyle={{ color: '#0F172A' }}
                    formatter={(value) => [formatCurrency(value), 'Premium']}
                  />
                  <Bar dataKey="total_premium" fill="#007AFF" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue by Product */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              REVENUE BY PRODUCT
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const hasClosedRevenue = productData.some(d => d.closed_premium > 0);
              const chartKey = hasClosedRevenue ? 'closed_premium' : 'pipeline_premium';
              const chartLabel = hasClosedRevenue ? 'Closed Premium' : 'Pipeline Premium';
              const chartData = productData.filter(d => d[chartKey] > 0);
              const total = chartData.reduce((sum, d) => sum + d[chartKey], 0);

              return chartData.length > 0 ? (
                <>
                  {!hasClosedRevenue && (
                    <p className="text-xs text-[#94A3B8] mb-1">Showing pipeline premium (no closed deals yet)</p>
                  )}
                  <div className="h-72 flex items-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="40%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey={chartKey}
                          nameKey="product_type"
                          label={({ value }) => total > 0 ? `${Math.round(value / total * 100)}%` : ''}
                          labelLine={false}
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                          formatter={(value) => [formatCurrency(value), chartLabel]}
                        />
                        <Legend 
                          verticalAlign="middle" 
                          align="right"
                          layout="vertical"
                          wrapperStyle={{ paddingLeft: '20px' }}
                          formatter={(value) => <span className="text-[#475569] text-sm">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </>
              ) : (
                <div className="h-72 flex items-center justify-center text-[#94A3B8]">
                  No product data available
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Opportunities by User */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#007AFF]" />
            <CardTitle className="text-lg text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              OPPORTUNITIES BY USER
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0] hover:bg-transparent bg-[#F8FAFC]">
                <TableHead className="text-[#475569] font-semibold">Team Member</TableHead>
                <TableHead className="text-[#475569] font-semibold text-center">Open Opps</TableHead>
                <TableHead className="text-[#475569] font-semibold text-right">Pipeline Premium</TableHead>
                <TableHead className="text-[#475569] font-semibold text-right">Closed Premium</TableHead>
                <TableHead className="text-[#475569] font-semibold text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamData.map((member) => (
                <TableRow key={member.user_id} className="border-[#E2E8F0] hover:bg-[#F8FAFC]">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9 border border-[#E2E8F0]">
                        <AvatarImage src={member.avatar_url} alt={member.name} />
                        <AvatarFallback className="bg-[#F1F5F9] text-[#007AFF] text-sm">
                          {member.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-[#0F172A]">{member.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className="bg-[#007AFF]/10 text-[#007AFF] border-0 font-bold text-base px-3">
                      {member.open_opportunities}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-[#007AFF] font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {formatCurrency(member.pipeline_premium || 0)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-[#10B981] font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {formatCurrency(member.closed_premium || 0)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-[#0F172A] font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {formatCurrency((member.pipeline_premium || 0) + (member.closed_premium || 0))}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {/* Totals Row */}
              <TableRow className="border-[#E2E8F0] bg-[#F8FAFC] font-bold">
                <TableCell>
                  <span className="font-bold text-[#0F172A]">TOTAL</span>
                </TableCell>
                <TableCell className="text-center">
                  <Badge className="bg-[#0F172A] text-white border-0 font-bold text-base px-3">
                    {teamData.reduce((sum, m) => sum + (m.open_opportunities || 0), 0)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-[#007AFF] font-bold text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {formatCurrency(teamData.reduce((sum, m) => sum + (m.pipeline_premium || 0), 0))}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-[#10B981] font-bold text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {formatCurrency(teamData.reduce((sum, m) => sum + (m.closed_premium || 0), 0))}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-[#0F172A] font-bold text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {formatCurrency(teamData.reduce((sum, m) => sum + (m.pipeline_premium || 0) + (m.closed_premium || 0), 0))}
                  </span>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Bottom Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Product Penetration by Sport */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              PRODUCT PENETRATION BY SPORT
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {penetrationData.map((sport) => (
                <div key={sport.sport} className="p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#0F172A] font-medium">{sport.sport}</span>
                    <Badge className="bg-[#007AFF]/10 text-[#007AFF] border-0">
                      {sport.total_accounts} programs
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(sport.product_penetration || {}).map(([product, pct]) => (
                      <Badge 
                        key={product} 
                        variant="outline" 
                        className="border-[#E2E8F0] text-[#475569] bg-white"
                      >
                        {product}: {pct}%
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
              {penetrationData.length === 0 && (
                <p className="text-[#94A3B8] text-center py-8">No penetration data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Activity KPIs */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              ACTIVITY KPIs (LAST 7 DAYS)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activityData.map((member) => (
                <div 
                  key={member.user_id} 
                  className="flex items-center gap-4 p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]"
                >
                  <Avatar className="h-10 w-10 border border-[#E2E8F0]">
                    <AvatarImage src={member.avatar_url} alt={member.name} />
                    <AvatarFallback className="bg-white text-[#007AFF]">
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#0F172A] font-medium truncate">{member.name}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <span className="text-xs text-[#475569]">
                        <span className="text-[#007AFF] font-semibold">{member.calls_per_week}</span> calls
                      </span>
                      <span className="text-xs text-[#475569]">
                        <span className="text-[#10B981] font-semibold">{member.meetings_per_week}</span> meetings
                      </span>
                      <span className="text-xs text-[#475569]">
                        <span className="text-[#F59E0B] font-semibold">{member.proposals_sent}</span> proposals
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#94A3B8] uppercase font-medium">Close Rate</p>
                    <p className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {member.proposal_to_close_rate}%
                    </p>
                  </div>
                </div>
              ))}
              {activityData.length === 0 && (
                <p className="text-[#94A3B8] text-center py-8">No activity data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
